/**
 * PublicSystemStatusWidget — customer-facing health badge.
 *
 * A discrete bottom-LEFT pill that polls /api/public/system-status
 * every 60 seconds (matches the backend cache window). Renders a
 * coloured dot (green / amber / red / gray) plus a short verdict
 * label. Clicking expands a panel that shows:
 *
 *   - The overall verdict + 24h sparkline + last verdict transition.
 *   - A per-area status breakdown (Reports & deliveries, Email,
 *     Partner integrations, Background automation) — each row has
 *     its own coloured dot and a hover tooltip describing what the
 *     customer may experience.
 *
 * Iter 246 moved the widget from bottom-right to bottom-left so it
 * stops fighting the scroll-to-top affordance on desktop. The pill
 * has plenty of horizontal real-estate to grow and the expanded
 * panel can open upward without bumping anything.
 *
 * Privacy-safe: all area copy is selected from a small pre-approved
 * set on the backend — internal collection / env / signal names are
 * never echoed to the customer.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const COLORS = {
  ready: {
    dot: 'bg-emerald-500',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    bg: 'bg-emerald-50',
    rowDot: 'bg-emerald-500',
  },
  warn: {
    dot: 'bg-amber-500',
    text: 'text-amber-800',
    border: 'border-amber-200',
    bg: 'bg-amber-50',
    rowDot: 'bg-amber-500',
  },
  fail: {
    dot: 'bg-red-500',
    text: 'text-red-800',
    border: 'border-red-200',
    bg: 'bg-red-50',
    rowDot: 'bg-red-500',
  },
  unknown: {
    dot: 'bg-slate-400',
    text: 'text-slate-700',
    border: 'border-slate-200',
    bg: 'bg-slate-50',
    rowDot: 'bg-slate-400',
  },
};

/**
 * Format an ISO timestamp into a compact, human-readable form.
 * - JA: `5月23日 20:15` (current year) or `2025年5月23日 20:15` (other year)
 * - EN: `May 23, 20:15` (current year) or `May 23, 2025, 20:15` (other year)
 *
 * Returns `'—'` for missing / unparseable values. Times are rendered in
 * Asia/Tokyo so the customer-facing widget always reads in JST regardless
 * of the visitor's local timezone.
 */
function formatTransitionAt(iso, ja) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const tz = 'Asia/Tokyo';
  const now = new Date();
  const sameYear =
    new Intl.DateTimeFormat('en-US', { year: 'numeric', timeZone: tz })
      .format(d) ===
    new Intl.DateTimeFormat('en-US', { year: 'numeric', timeZone: tz })
      .format(now);
  const opts = {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };
  if (!sameYear) opts.year = 'numeric';
  if (ja) {
    opts.month = 'long';
    opts.day = 'numeric';
    return new Intl.DateTimeFormat('ja-JP', opts).format(d);
  }
  opts.month = 'short';
  opts.day = 'numeric';
  return new Intl.DateTimeFormat('en-US', opts).format(d);
}

function MiniSparkline({ data, color }) {
  if (!Array.isArray(data) || data.length === 0) return null;
  const w = 96;
  const h = 18;
  const max = 1;
  const step = w / Math.max(1, data.length - 1);
  const pts = data
    .map(
      (v, i) =>
        `${(i * step).toFixed(1)},${(h - (v / max) * h).toFixed(1)}`,
    )
    .join(' ');
  return (
    <svg
      width={w}
      height={h}
      className={`inline-block align-middle ${color}`}
      aria-hidden="true"
    >
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        points={pts}
      />
    </svg>
  );
}

function AreaRow({ area, ja }) {
  const c = COLORS[area.verdict] || COLORS.unknown;
  const label = ja ? area.label_ja : area.label_en;
  const detail = ja ? area.detail_ja : area.detail_en;
  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="flex items-center justify-between gap-3 py-1.5 px-2 -mx-2 rounded hover:bg-slate-50 cursor-help"
            data-testid={`public-system-status-area-${area.key}`}
            data-verdict={area.verdict}
          >
            <span className="flex items-center gap-2 text-slate-700">
              <span
                className={`inline-block w-2 h-2 rounded-full ${c.rowDot} ${
                  area.verdict === 'fail' ? 'animate-pulse' : ''
                }`}
                aria-hidden="true"
              />
              <span className="text-[11px] font-medium">{label}</span>
            </span>
            <span
              className={`text-[10px] uppercase tracking-wide font-semibold ${c.text}`}
            >
              {area.verdict === 'ready'
                ? ja
                  ? '正常'
                  : 'OK'
                : area.verdict === 'warn'
                ? ja
                  ? '注意'
                  : 'Warn'
                : area.verdict === 'fail'
                ? ja
                  ? '障害'
                  : 'Issue'
                : '—'}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          align="start"
          className="max-w-[280px]"
          data-testid={`public-system-status-area-tooltip-${area.key}`}
        >
          <div className={`text-[11px] font-semibold mb-1 ${c.text}`}>
            {label}
          </div>
          <div className="text-[11px] leading-relaxed text-slate-700">
            {detail}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function PublicSystemStatusWidget({ ja = false }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [lastFetchAt, setLastFetchAt] = useState(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const containerRef = useRef(null);

  const fetchOnce = useCallback(async () => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const r = await fetch(`${base}/api/public/system-status`, {
        credentials: 'omit',
        headers: { Accept: 'application/json' },
      });
      if (!r.ok) return;
      const body = await r.json();
      setData(body);
      setLastFetchAt(Date.now());
    } catch (_) {
      // silent — widget should never surface its own errors to customers
    }
  }, []);

  useEffect(() => {
    fetchOnce();
    const t = setInterval(fetchOnce, 60_000);
    return () => clearInterval(t);
  }, [fetchOnce]);

  // Auto-refresh when the tab regains focus — so someone alt-tabbing
  // back after several minutes sees current data within ~1s instead of
  // waiting up to 60s for the next polling tick. Gated by a 10s
  // staleness threshold so quick tab switches don't spam the endpoint.
  useEffect(() => {
    const refreshIfStale = () => {
      const age = lastFetchAt ? Date.now() - lastFetchAt : Infinity;
      if (age > 10_000) fetchOnce();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshIfStale();
    };
    window.addEventListener('focus', refreshIfStale);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', refreshIfStale);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchOnce, lastFetchAt]);

  // Tick once per second while the panel is open so the "Last check"
  // relative-time indicator stays live without re-fetching.
  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open]);

  // Click-outside-to-close: when the panel is open, any mousedown
  // outside the widget container collapses it. Also handle Escape.
  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  if (!data) return null;

  const c = COLORS[data.verdict] || COLORS.unknown;
  const label = ja ? data.verdict_label_ja : data.verdict_label_en;
  // Collapsed-button label is ONE WORD only. The colored dot conveys
  // verdict; the full sentence appears in the expanded panel.
  const pillLabel = ja ? '状態' : 'Status';
  const areas = Array.isArray(data.areas) ? data.areas : [];

  // Human-friendly "X ago" string from lastFetchAt. nowTick is bumped
  // every second while the panel is open so this re-renders live.
  const ageSec = lastFetchAt
    ? Math.max(0, Math.floor((nowTick - lastFetchAt) / 1000))
    : null;
  let ageLabel = '';
  if (ageSec !== null) {
    if (ageSec < 5) ageLabel = ja ? 'たった今' : 'Just now';
    else if (ageSec < 60) ageLabel = ja ? `${ageSec}秒前` : `${ageSec}s ago`;
    else {
      const m = Math.floor(ageSec / 60);
      ageLabel = ja ? `${m}分前` : `${m}m ago`;
    }
  }

  return (
    <div
      ref={containerRef}
      className="fixed top-[10px] left-5 z-[9999] hidden sm:block"
      data-testid="public-system-status-widget"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className={`flex items-center gap-1 px-1.5 py-[1px] rounded-full shadow-sm border ${c.border} ${c.bg} ${c.text} text-[9px] font-semibold hover:shadow-md transition leading-none`}
        data-testid="public-system-status-pill"
        aria-label={`System status: ${label}`}
        aria-expanded={open}
      >
        <span
          className={`inline-block w-1.5 h-1.5 rounded-full ${c.dot} ${
            data.verdict === 'fail' ? 'animate-pulse' : ''
          }`}
        />
        <span data-testid="public-system-status-label">{pillLabel}</span>
      </button>

      {open && (
        <div
          className={`mt-2 p-3 rounded-md shadow-lg border ${c.border} bg-white text-xs space-y-2 w-80`}
          data-testid="public-system-status-panel"
        >
          <div className="flex items-center justify-between">
            <div className={`font-semibold ${c.text}`}>{label}</div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-slate-400 hover:text-slate-600 text-[14px] leading-none px-1"
              aria-label={ja ? '閉じる' : 'Close'}
              data-testid="public-system-status-close"
            >
              ×
            </button>
          </div>

          {ageLabel && (
            <div
              className="flex items-center gap-1.5 text-[10px] text-slate-500 -mt-1"
              data-testid="public-system-status-last-check"
              title={
                lastFetchAt
                  ? new Date(lastFetchAt).toLocaleString()
                  : undefined
              }
            >
              <span
                className={`inline-block w-1.5 h-1.5 rounded-full ${
                  ageSec !== null && ageSec < 65
                    ? 'bg-emerald-500 animate-pulse'
                    : 'bg-slate-400'
                }`}
                aria-hidden="true"
              />
              <span>
                {ja ? '最終確認' : 'Last check'}: {ageLabel}
              </span>
            </div>
          )}

          {areas.length > 0 && (
            <div
              className="pt-1 border-t border-slate-100 space-y-0.5"
              data-testid="public-system-status-areas"
            >
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1 px-2 -mx-2">
                {ja ? 'エリア別ステータス' : 'Status by area'}
              </div>
              {areas.map((a) => (
                <AreaRow key={a.key} area={a} ja={ja} />
              ))}
              <div className="text-[10px] text-slate-400 px-2 -mx-2 pt-1 italic">
                {ja
                  ? '行にカーソルを合わせると詳細を表示'
                  : 'Hover any row for details'}
              </div>
            </div>
          )}

          {data.sparkline_24h && (
            <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-100">
              <span className="text-slate-500">
                {ja ? '24時間の推移' : '24h trend'}
              </span>
              <MiniSparkline data={data.sparkline_24h} color={c.text} />
            </div>
          )}

          {data.last_transition && data.last_transition.to && (
            <div className="text-slate-500">
              {ja ? '最終更新' : 'Last change'}:{' '}
              <span className="font-mono">
                {data.last_transition.from || '—'} →{' '}
                {data.last_transition.to}
              </span>
              {' · '}
              <span>{formatTransitionAt(data.last_transition.at, ja)}</span>
            </div>
          )}

          <div className="text-slate-400 text-[10px]">
            {ja ? '60秒ごとに自動更新' : 'Auto-refreshes every 60s'}
          </div>
        </div>
      )}
    </div>
  );
}
